import { Position3D } from '@digital-bridge/fine';
import { FurnitureDescription } from '../../../data/furnitureDescription';
import * as Sofa from '../../../data/modaSofa';
import * as P from '../../../data/pose';
import * as E from 'fp-ts/Either';

/** Description of a Furniture and its Placement within the Scene. Used in the Arya Interface */
export type FurniturePlacement = {
    productId: string;
    description: FurnitureDescription;
    pose: P.Pose;
};

type Piece = Sofa.ModaSofa.Piece.Piece;

const poseFromPiece = (piece: Piece) =>
    P.construct({
        position: new Position3D(piece.position.x(), 0, piece.position.y()),
        orientation: piece.orientation,
    });

export const fromPiece = (
    piece: Piece,
    furnitureBySku: Record<string, FurnitureDescription>,
): FurniturePlacement => {
    return {
        productId: piece.sku,
        description: furnitureBySku[piece.sku],
        pose: poseFromPiece(piece),
    };
};

/** 'Doctor' the end-piece articles such that only one can be valid at a time. See docs/sofaEndPieceArticles.md for details */
export const stripEndPieceArticles = (
    pieces: Piece[],
    articleToKeep: 'sofaEndPieceLeft' | 'sofaEndPieceRight',
    furnitureBySku: Record<string, FurnitureDescription>,
): FurniturePlacement[] =>
    pieces.map(piece => {
        const description: FurnitureDescription | undefined = furnitureBySku[piece.sku];
        return {
            productId: piece.sku,
            // we're modifying the description such that there's only one article in any given variant.
            description: {
                ...description,
                variants: description?.variants.map(v => ({
                    ...v,
                    articles: { [articleToKeep]: v.articles[articleToKeep] },
                })),
            },
            pose: poseFromPiece(piece),
        };
    });

/** Construct a series of FurniturePlacements from an array of Pieces */
export const fromPieces = (
    pieces: Piece[],
    furnitureBySku: Record<string, FurnitureDescription>,
) => {
    const furnitureToAdd = pieces
        .filter(p => p.kind !== 'sofa-end-left' && p.kind !== 'sofa-end-right')
        .map((piece): FurniturePlacement => fromPiece(piece, furnitureBySku));

    /**
     * This next part is necessary to ensure Arya displays end pieces correctly. See
     * the documentation in `docs/sofaEndPieceArticles.md` for full details
     */
    const leftPieces = stripEndPieceArticles(
        pieces.filter(p => p.kind === 'sofa-end-left'),
        'sofaEndPieceLeft',
        furnitureBySku,
    );

    const rightPieces = stripEndPieceArticles(
        pieces.filter(p => p.kind === 'sofa-end-right'),
        'sofaEndPieceRight',
        furnitureBySku,
    );

    furnitureToAdd.push(...leftPieces, ...rightPieces);

    // see if we're missing any furniture descriptions
    const missingFurniture = furnitureToAdd.filter(
        furniture => furniture.description === undefined,
    );
    if (missingFurniture.length > 0) {
        return E.left(
            `failed to add modular sofa, cannot find furniture descriptions for all pieces ${missingFurniture
                .map(f => f.productId)
                .join(', ')}`,
        );
    } else {
        return E.right(furnitureToAdd);
    }
};
